/**
 * Centralized analytics event layer.
 *
 * Fires events to GA4 (gtag), Meta Pixel (fbq), and TikTok Pixel (ttq) in one call.
 * Each pixel is a no-op if its script hasn't loaded (env var was empty / blocked).
 *
 * GA4 key events (mark these in Admin → Data display → Events → Mark as key event):
 *   - generate_lead   ← email_capture
 *   - add_to_cart       ← buy_button_click
 *   - begin_checkout    ← checkout_initiated
 *   - purchase          ← purchase (success page, deduped per session_id)
 *
 * Other events:
 *   - page_view (auto on load + SPA route changes via trackPageView)
 *   - scroll_50 / scroll_90 (auto, in main.tsx)
 *   - section_view (funnel sections, in main.tsx)
 */

type AnyFn = (...args: unknown[]) => unknown;

interface AnalyticsWindow {
  gtag?: AnyFn;
  fbq?: AnyFn;
  ttq?: { track?: AnyFn };
  clarity?: AnyFn;
  dataLayer?: unknown[];
  __GA_MEASUREMENT_ID__?: string;
}

function w(): AnalyticsWindow {
  return (typeof window !== 'undefined' ? window : {}) as unknown as AnalyticsWindow;
}

function ga4Item(type: string, priceDollars: number) {
  return {
    item_id: `trojan-${type}`,
    item_name: `Trojan ${type}`,
    price: priceDollars,
    quantity: 1,
  };
}

function toDollars(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // Checkout amounts are cents; success-page values are already dollars.
  return n >= 100 ? n / 100 : n;
}

/** Mirror funnel events to GA4 recommended names so they qualify as key events. */
function trackGA4KeyEvent(eventName: string, params: Record<string, unknown>): string | null {
  const win = w();
  if (typeof win.gtag !== 'function') return null;

  const type = String(params.type ?? params.product ?? 'book');
  const value = toDollars(params.value ?? params.amount ?? params.price);

  switch (eventName) {
    case 'email_capture':
      win.gtag('event', 'generate_lead', {
        lead_source: params.lead_source,
        value,
        currency: 'USD',
      });
      return 'generate_lead';
    case 'buy_button_click':
      win.gtag('event', 'add_to_cart', {
        location: params.location,
        method: params.method,
        currency: 'USD',
        value: value || undefined,
        items: [ga4Item(type, value || 9.99)],
      });
      return 'add_to_cart';
    case 'checkout_initiated':
    case 'membership_checkout_started':
      win.gtag('event', 'begin_checkout', {
        currency: 'USD',
        value,
        coupon: params.coupon,
        items: [ga4Item(type, value)],
      });
      return 'begin_checkout';
    case 'purchase':
    case 'membership_purchase': {
      const purchaseParams = {
        transaction_id: params.transaction_id,
        value,
        currency: params.currency ?? 'USD',
        items: Array.isArray(params.items) ? params.items : [ga4Item(type, value)],
      };
      win.gtag('event', 'purchase', purchaseParams);
      return 'purchase';
    }
    default:
      return null;
  }
}

/** GA4 page views for SPA route changes (standard reports + Intelligence). */
export function trackPageView(pagePath: string, pageTitle?: string) {
  const win = w();
  const gaId = win.__GA_MEASUREMENT_ID__;
  if (typeof win.gtag !== 'function' || !gaId) return;

  const params: Record<string, string> = {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
  };
  if (pageTitle) params.page_title = pageTitle;

  win.gtag('event', 'page_view', params);
}

export function trackEvent(eventName: string, params: Record<string, unknown> = {}) {
  const win = w();

  // Google Analytics 4 — custom event name + recommended key-event mirror
  try {
    if (typeof win.gtag === 'function') {
      const ga4KeyEvent = trackGA4KeyEvent(eventName, params);
      if (!ga4KeyEvent || ga4KeyEvent !== eventName) {
        win.gtag('event', eventName, params);
      }
    }
  } catch (e) {
    console.warn('[analytics] gtag failed:', e);
  }

  // Meta Pixel — map our events to standard Meta events where useful
  try {
    if (typeof win.fbq === 'function') {
      const metaMap: Record<string, string> = {
        email_capture: 'Lead',
        trojan_index_completed: 'Lead',
        buy_button_click: 'InitiateCheckout',
        checkout_initiated: 'InitiateCheckout',
        membership_checkout_started: 'InitiateCheckout',
        purchase: 'Purchase',
        membership_purchase: 'Purchase',
      };
      const metaEvent = metaMap[eventName];
      if (metaEvent) win.fbq('track', metaEvent, params);
      else win.fbq('trackCustom', eventName, params);
    }
  } catch (e) {
    console.warn('[analytics] fbq failed:', e);
  }

  // TikTok Pixel
  try {
    if (win.ttq && typeof win.ttq.track === 'function') {
      const tiktokMap: Record<string, string> = {
        email_capture: 'CompleteRegistration',
        trojan_index_completed: 'CompleteRegistration',
        buy_button_click: 'InitiateCheckout',
        checkout_initiated: 'InitiateCheckout',
        membership_checkout_started: 'InitiateCheckout',
        membership_purchase: 'Purchase',
        purchase_success_page_viewed: 'Purchase',
      };
      const tiktokEvent = tiktokMap[eventName] || eventName;
      // TikTok requires content_id and contents for checkout/purchase events
      const tiktokParams = ['InitiateCheckout', 'Purchase'].includes(tiktokEvent)
        ? {
            ...params,
            contents: [{
              content_id: String(params.type ?? 'trojan-book'),
              content_name: String(params.type ?? 'Trojan'),
              quantity: 1,
            }],
            content_type: 'product',
          }
        : params;
      win.ttq.track(tiktokEvent, tiktokParams);
    }
  } catch (e) {
    console.warn('[analytics] ttq failed:', e);
  }

  // Microsoft Clarity — forward meaningful actions as custom events. These show up
  // in Clarity as named events you can filter recordings on AND use as funnel steps,
  // replacing the generic auto-detected ones. We skip high-frequency auto signals so
  // the custom-event list stays meaningful.
  try {
    const noisyForClarity = new Set(['scroll_50', 'scroll_90', 'section_view', 'page_view']);
    if (typeof win.clarity === 'function' && !noisyForClarity.has(eventName)) {
      win.clarity('event', eventName);
      // Attach the most useful param as a tag so recordings are segmentable
      // (e.g. by which CTA location drove the click).
      const tagValue = params.location ?? params.lead_source ?? params.variant ?? params.product ?? params.type;
      if (tagValue != null) win.clarity('set', eventName, String(tagValue));
    }
  } catch (e) {
    console.warn('[analytics] clarity failed:', e);
  }
}

/**
 * Microsoft Clarity custom tags, set once per session. Powers two dashboard needs:
 *
 *  1. Internal-traffic exclusion (more reliable than IP blocking for a mobile/remote
 *     team): on a team device, visit any page once with `?internal=1`. We persist it
 *     and tag every later session `traffic=internal`. In Clarity, add a filter
 *     "traffic ≠ internal" (save it as your default segment) to drop team sessions.
 *     Visit `?internal=0` to clear the flag on that device.
 *
 *  2. A `page_type` tag (landing vs. checkout vs. other) so recordings/heatmaps are
 *     segmentable by funnel stage.
 *
 * Clarity loads asynchronously, so we poll briefly for the global before tagging.
 */
export function initClarityTags() {
  if (typeof window === 'undefined') return;

  const apply = () => {
    const win = w();
    if (typeof win.clarity !== 'function') return false;
    try {
      const search = new URLSearchParams(window.location.search);
      if (search.get('internal') === '1') localStorage.setItem('trojan_internal', '1');
      if (search.get('internal') === '0') localStorage.removeItem('trojan_internal');
      const internal = localStorage.getItem('trojan_internal') === '1';
      win.clarity('set', 'traffic', internal ? 'internal' : 'visitor');
    } catch {
      // localStorage blocked (private mode) — still tag traffic as visitor.
      win.clarity('set', 'traffic', 'visitor');
    }

    const path = window.location.pathname;
    const pageType = path === '/'
      ? 'landing'
      : path.startsWith('/purchase') || path.startsWith('/membership/success')
        ? 'checkout'
        : path === '/membership'
          ? 'membership'
          : path.startsWith('/members')
            ? 'member_area'
            : path.startsWith('/trojan-index')
              ? 'lead_magnet'
              : 'other';
    win.clarity('set', 'page_type', pageType);
    return true;
  };

  if (apply()) return;
  let tries = 0;
  const tick = () => {
    if (apply() || tries++ >= 20) return;
    setTimeout(tick, 250);
  };
  setTimeout(tick, 250);
}

/**
 * Append the current URL's utm_* params to an outbound URL so we can attribute
 * Amazon / Apple Books referrals back to the campaign that drove the original click.
 */
export function withUtms(url: string): string {
  try {
    const here = new URL(window.location.href);
    const target = new URL(url, window.location.href);
    here.searchParams.forEach((v, k) => {
      if (k.startsWith('utm_') && !target.searchParams.has(k)) {
        target.searchParams.set(k, v);
      }
    });
    return target.toString();
  } catch {
    return url;
  }
}

/**
 * Scroll-depth listeners. Fires scroll_50 and scroll_90 once per session.
 */
export function initScrollDepthTracking() {
  if (typeof window === 'undefined') return;
  let fired50 = false;
  let fired90 = false;

  const onScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = (scrollTop / docHeight) * 100;

    if (!fired50 && pct >= 50) {
      fired50 = true;
      trackEvent('scroll_50');
    }
    if (!fired90 && pct >= 90) {
      fired90 = true;
      trackEvent('scroll_90');
      window.removeEventListener('scroll', onScroll);
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
}

/** Fires once when a funnel section enters the viewport — powers GA4 funnel/exploration reports. */
export function initSectionVisibilityTracking(sectionIds: string[]) {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  const seen = new Set<string>();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const id = entry.target.id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        trackEvent('section_view', { section_id: id });
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.35 },
  );

  for (const id of sectionIds) {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  }
}
